import { useMemo, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Raven from 'raven-js';
import { useLocation } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import PortalIdParser from 'PortalIdParser';
import styled from 'styled-components';
import { getAuthorizationDataStatus, getApplicationAuthorizationData, getInternallyDevelopedAppIdsStatus, getIsInternallyDeveloped, getIsRequestingNewScopesStatus, getAuthorizeApplicationErrorSubCategory, getAuthorizeApplicationErrorContext, getAuthorizationSource, getAuthorizeStatus, getFullAppDetails, getFullAppDetailsStatus } from '../../selectors/AppSelectors';
import AuthorizeHeader from '../AuthorizeHeader';
import LoadingStateWrapper from '../LoadingStateWrapper';
import AuthorizeBody from '../AuthorizeBody';
import { fetchApplicationAuthorizationData, fetchFullAppDetails, fetchIsRequestingNewScopes } from '../../actions/AppActions';
import { getAuthorizationRequestInfo } from '../../utils/urls';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { getShouldAllowDevPortalInstall, isValidClientId } from '../../utils/authUtils';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { getHasOnlyDevelopersParcel } from '../../selectors/ScopeSelectors';
import { fetchEcosystemDetails } from '../../actions/EcosystemActions';
import { getEcosystemDetailsStatus } from '../../selectors/EcosystemSelectors';
import { LISTING_STATUS } from '../../data/models/ApplicationAuthorization';
import EmbeddedContext from '../../utils/EmbeddedContext';
import { AuthorizationSources } from '../../constants/AuthorizationSources';
import { useFetchCurrentAccount } from '../../hooks/queries/accounts/useFetchCurrentAccount';
import { Link } from 'foundations-components/transitional/components/Link';
import { getListingStatus } from '../../utils/appUtils';
import { getNormalizedQuery } from '../../utils/urls';
import { parse } from 'hub-http/helpers/params';
import MissingParameters from '../MissingParameters';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RelativeDiv = styled.div.withConfig({
  displayName: "AuthorizeContainer__RelativeDiv",
  componentId: "zz0cw4-0"
})(["position:relative;"]);
const AuthorizeContainer = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    allowParentRedirect,
    sendMessage
  } = useContext(EmbeddedContext);
  const authorizationDataStatus = useSelector(getAuthorizationDataStatus);
  const authorizationData = useSelector(getApplicationAuthorizationData);
  const internallyDevelopedStatus = useSelector(getInternallyDevelopedAppIdsStatus);
  const isInternallyDeveloped = useSelector(getIsInternallyDeveloped);
  const hasOnlyDevelopersParcel = useSelector(getHasOnlyDevelopersParcel);
  const authorizationSource = useSelector(getAuthorizationSource);
  const authorizeStatus = useSelector(getAuthorizeStatus);
  const isRequestingNewScopesStatus = useSelector(getIsRequestingNewScopesStatus);
  const authorizeApplicationErrorSubCategory = useSelector(getAuthorizeApplicationErrorSubCategory);
  const authorizeApplicationErrorContext = useSelector(getAuthorizeApplicationErrorContext);
  const fullAppDetails = useSelector(getFullAppDetails);
  const fullAppDetailsStatus = useSelector(getFullAppDetailsStatus);
  const shouldShowLoadingOverlay = authorizationSource === AuthorizationSources.PREVIOUSLY_AUTHORIZED && authorizationDataStatus.succeeded && authorizeStatus.pending;
  const shouldAllowDevPortalInstall = useMemo(() => authorizationDataStatus.succeeded && getShouldAllowDevPortalInstall(authorizationData), [authorizationData, authorizationDataStatus]);
  const authorizationRequestInfo = useMemo(() => getAuthorizationRequestInfo(location.search), [location.search]);
  const clientId = authorizationRequestInfo.clientId;
  const clientIdIsValid = isValidClientId(clientId);
  const {
    requestStatus: accountRequestStatus,
    data: account
  } = useFetchCurrentAccount();
  useEffect(() => {
    if (clientIdIsValid) {
      dispatch(fetchFullAppDetails(clientId)).catch(e => Raven.captureException(e));
      dispatch(fetchApplicationAuthorizationData({
        data: authorizationRequestInfo,
        redirectOptions: {
          allowParentRedirect,
          sendMessage
        }
      })).catch(e => Raven.captureException(e));
      dispatch(fetchIsRequestingNewScopes(authorizationRequestInfo)).catch(e => Raven.captureException(e));
    }
  }, [dispatch, authorizationRequestInfo, clientIdIsValid, allowParentRedirect, sendMessage, clientId]);
  const ecosystemDetailsStatus = useSelector(getEcosystemDetailsStatus);
  const listingStatus = getListingStatus(fullAppDetails === null || fullAppDetails === void 0 ? void 0 : fullAppDetails.listingInfo);
  const isOnMarketplace = authorizationDataStatus.succeeded && authorizationData && listingStatus !== LISTING_STATUS.UNLISTED;
  useEffect(() => {
    if (isOnMarketplace) {
      dispatch(fetchEcosystemDetails(authorizationData.appId)).catch(e => Raven.captureException(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnMarketplace]);
  const renderAlert = ({
    rhumbName,
    content
  }) => {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: rhumbName,
      children: /*#__PURE__*/_jsx(Alert, {
        titleText: authorizeApplicationErrorSubCategory === 'AuthorizationErrorType.USER_MISSING_APP_MARKETPLACE_ACCESS_PERMISSION' ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "oauth.app.marketplaceAccess.needPermissionTitle"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "oauth.authorization.errorSubHeader"
        }),
        type: "danger",
        marginBlock: "400",
        children: content
      })
    });
  };
  if (!clientIdIsValid || !account && accountRequestStatus.failed) {
    return /*#__PURE__*/_jsx(TFlex, {
      align: "center",
      justify: "center",
      direction: "column",
      children: renderAlert({
        rhumbName: 'AUTH_PAGE_LOAD',
        content: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "oauth.errors.BAD_CLIENT_ID"
        })
      })
    });
  }
  const renderMessageForErrorCode = () => {
    if (authorizeApplicationErrorSubCategory === 'AuthorizationErrorType.USER_MISSING_APP_MARKETPLACE_ACCESS_PERMISSION') {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "oauth.app.marketplaceAccess.needPermissionBody_jsx",
        elements: {
          Link
        },
        options: {
          href: 'https://knowledge.hubspot.com/user-management/hubspot-user-permissions-guide'
        }
      });
    }
    const potentialI18nKey = `oauth.${authorizeApplicationErrorSubCategory}`;
    return isValidI18nKey(potentialI18nKey) ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: potentialI18nKey,
      options: authorizeApplicationErrorContext
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "oauth.AuthorizationErrorType.UNEXPECTED_ERROR"
    });
  };
  const renderMissingParamError = ({
    missingParameters
  }) => {
    return /*#__PURE__*/_jsx(MissingParameters, {
      missingParameters: missingParameters
    });
  };
  const renderAuthContent = () => {
    if (authorizationDataStatus.pending || authorizationDataStatus.uninitialized) {
      return null;
    }
    if (!account) {
      return null;
    }

    // Check scope query param before anything else
    const params = Object.keys(getNormalizedQuery(parse(location.search.slice(1))));
    const missingParameters = ['scope'].filter(value => !params.includes(value));
    if (missingParameters.length > 0 && (fullAppDetails === null || fullAppDetails === void 0 ? void 0 : fullAppDetails.isUserLevel) !== true) {
      return renderMissingParamError({
        missingParameters
      });
    }
    if (authorizationDataStatus.warning) {
      // There was a non-server related error with the App Auth (user isn't authorized, wrong client_id, etc)
      return renderAlert({
        rhumbName: 'AUTH_PAGE_LOAD',
        content: renderMessageForErrorCode()
      });
    } else if (hasOnlyDevelopersParcel && !shouldAllowDevPortalInstall) {
      // We're blocking an install into a developer portal
      return renderAlert({
        rhumbName: 'AUTH_PAGE_LOAD',
        content: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          options: {
            portalId: `${PortalIdParser.get()}`
          },
          message: `oauth.errors.DEVELOPER_PORTAL`
        })
      });
    } else {
      // We successfully authed
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "AUTH_PAGE_LOAD",
        children: /*#__PURE__*/_jsxs(RelativeDiv, {
          children: [/*#__PURE__*/_jsx(AuthorizeHeader
          // App logo
          , {
            providerIconUrl: authorizationData.listingIconUrl,
            providerName: authorizationData.name
          }), shouldShowLoadingOverlay ? /*#__PURE__*/_jsx(LoadingOverlay, {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "oauth.authorization.previouslyAuthenticated"
            }),
            showLabel: true,
            layout: "centered"
          }) : /*#__PURE__*/_jsx(AuthorizeBody, {
            authorizationData: authorizationData,
            authorizationDataStatus: authorizationDataStatus,
            isInternallyDeveloped: isInternallyDeveloped,
            account: account,
            fullAppDetails: fullAppDetails
          })]
        })
      });
    }
  };
  return /*#__PURE__*/_jsxs(TFlex, {
    align: "center",
    justify: "center",
    direction: "column",
    children: [/*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx(LoadingStateWrapper, {
      status: authorizationDataStatus.succeeded ? [internallyDevelopedStatus, isRequestingNewScopesStatus, accountRequestStatus, fullAppDetailsStatus, ...(isOnMarketplace ? [ecosystemDetailsStatus] : [])] : authorizationDataStatus,
      showLoadingIfUnfetched: true,
      error: renderAlert({
        rhumbName: 'AUTH_PAGE_LOAD_ERROR',
        content: renderMessageForErrorCode()
      }),
      children: renderAuthContent()
    })]
  });
};
export default AuthorizeContainer;